import { useState, useEffect } from "react";
import clsx from "clsx";
import api from "@/api/client";

/* ─── Institution category definitions ───────────────────────────────────── */
const CATEGORIES = [
  {
    id: "school",
    label: "Schule",
    sub: "Volksschule, Mittelschule, Gymnasium…",
    color: "#8b5cf6",
    bg: "rgba(139,92,246,0.12)",
    border: "rgba(139,92,246,0.35)",
    glow: "rgba(139,92,246,0.2)",
    Icon: () => (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="1.6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  },
  {
    id: "center",
    label: "Förderzentrum",
    sub: "Lernförderung, Integration…",
    color: "#06b6d4",
    bg: "rgba(6,182,212,0.12)",
    border: "rgba(6,182,212,0.35)",
    glow: "rgba(6,182,212,0.2)",
    Icon: () => (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="1.6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.05 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.21 9.77a16 16 0 0 0 6 6l1.13-1.09a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16.92z"/>
      </svg>
    ),
  },
  {
    id: "youth",
    label: "Jugendzentrum",
    sub: "Nachmittagsbetreuung, Sozialarbeit…",
    color: "#22c55e",
    bg: "rgba(34,197,94,0.12)",
    border: "rgba(34,197,94,0.35)",
    glow: "rgba(34,197,94,0.2)",
    Icon: () => (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="1.6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    id: "therapy",
    label: "Therapiezentrum",
    sub: "Ergotherapie, Logopädie, Psychologie…",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.12)",
    border: "rgba(245,158,11,0.35)",
    glow: "rgba(245,158,11,0.2)",
    Icon: () => (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="1.6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    ),
  },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

/* ─── Animated starfield ──────────────────────────────────────────────────── */
const STARS = Array.from({ length: 80 }, (_, i) => ({
  id: i, x: Math.random() * 100, y: Math.random() * 100,
  r: Math.random() * 1.4 + 0.3, delay: Math.random() * 6, dur: 2 + Math.random() * 4,
}));

/* ─── Step indicator ─────────────────────────────────────────────────────── */
function StepDot({ active, done }) {
  return (
    <div className={clsx(
      "w-2 h-2 rounded-full transition-all duration-300",
      done ? "bg-violet-400 scale-110" : active ? "bg-violet-500 scale-125 shadow-lg shadow-violet-500/50" : "bg-gray-700"
    )}/>
  );
}

/* ─── Category Card ──────────────────────────────────────────────────────── */
function CategoryCard({ cat, onClick, delay }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className="relative group flex flex-col gap-3 p-5 rounded-2xl border text-left transition-all duration-300"
      style={{
        background: hov ? cat.bg : "rgba(15,15,30,0.6)",
        borderColor: hov ? cat.border : "rgba(255,255,255,0.06)",
        transform: hov ? "translateY(-2px)" : "none",
        boxShadow: hov ? `0 12px 40px ${cat.glow}` : "none",
        transitionDelay: `${delay}ms`,
      }}
    >
      {/* Glow sweep on hover */}
      <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
        <div className="absolute inset-0 transition-opacity duration-300" style={{
          opacity: hov ? 1 : 0,
          background: `radial-gradient(ellipse at 0% 0%, ${cat.glow}, transparent 70%)`,
        }}/>
      </div>

      <div className="relative flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300"
          style={{ background: hov ? cat.bg : "rgba(255,255,255,0.04)", color: hov ? cat.color : "#6b7280" }}>
          <cat.Icon/>
        </div>
        <div>
          <p className="font-black text-sm transition-colors duration-200" style={{ color: hov ? "#fff" : "#d1d5db" }}>{cat.label}</p>
          <p className="text-[10px] text-gray-600 mt-0.5 leading-tight">{cat.sub}</p>
        </div>
        <svg className="ml-auto flex-shrink-0 transition-all duration-200"
          style={{ color: hov ? cat.color : "#374151", transform: hov ? "translateX(2px)" : "none" }}
          width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </div>
    </button>
  );
}

/* ─── Institution Row ────────────────────────────────────────────────────── */
function InstitutionRow({ inst, cat, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border text-left transition-all duration-200"
      style={{
        background: hov ? cat.bg : "transparent",
        borderColor: hov ? cat.border : "rgba(255,255,255,0.05)",
        transform: hov ? "translateX(2px)" : "none",
      }}
    >
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200"
        style={{ background: hov ? cat.bg : "rgba(255,255,255,0.04)", color: hov ? cat.color : "#6b7280" }}>
        <cat.Icon/>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate" style={{ color: hov ? "#fff" : "#d1d5db" }}>{inst.name}</p>
        {inst.city && <p className="text-[10px] text-gray-600">{inst.city}</p>}
      </div>
      {hov && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" style={{ color: cat.color, flexShrink: 0 }}>
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      )}
    </button>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function InstitutionSelect({ onSelect, onPrivate }) {
  const [step, setStep]               = useState("category"); // "category" | "list"
  const [selectedCat, setSelectedCat] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [entered, setEntered]         = useState(false);

  useEffect(() => { const t = setTimeout(() => setEntered(true), 60); return () => clearTimeout(t); }, []);

  async function handleCategoryClick(cat) {
    setSelectedCat(cat);
    setStep("list");
    setLoading(true);
    setSearch("");
    try {
      const res = await api.get(`/institutions?type=${cat.id}`);
      setInstitutions(res.data.institutions || []);
    } catch {
      setInstitutions([]);
    } finally {
      setLoading(false);
    }
  }

  const cat = selectedCat ? CAT_MAP[selectedCat.id] : null;

  const filtered = institutions.filter((i) =>
    !search || i.name.toLowerCase().includes(search.toLowerCase()) || (i.city || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-dvh bg-nexus-bg flex flex-col items-center justify-center relative overflow-hidden px-4 py-8">
      {/* Deep space background */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <radialGradient id="bg1" cx="20%" cy="20%"><stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.14"/><stop offset="100%" stopColor="#8b5cf6" stopOpacity="0"/></radialGradient>
            <radialGradient id="bg2" cx="80%" cy="80%"><stop offset="0%" stopColor="#06b6d4" stopOpacity="0.10"/><stop offset="100%" stopColor="#06b6d4" stopOpacity="0"/></radialGradient>
            <radialGradient id="bg3" cx="50%" cy="50%"><stop offset="0%" stopColor="#ec4899" stopOpacity="0.06"/><stop offset="100%" stopColor="#ec4899" stopOpacity="0"/></radialGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#bg1)"/>
          <rect width="100%" height="100%" fill="url(#bg2)"/>
          <rect width="100%" height="100%" fill="url(#bg3)"/>
          {STARS.map((s) => (
            <circle key={s.id} cx={`${s.x}%`} cy={`${s.y}%`} r={s.r} fill="white" opacity={0}>
              <animate attributeName="opacity" values="0.05;0.8;0.05" dur={`${s.dur}s`} begin={`${s.delay}s`} repeatCount="indefinite"/>
            </circle>
          ))}
        </svg>
        {/* Grid overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(139,92,246,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.03) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}/>
      </div>

      {/* Content */}
      <div className={clsx(
        "relative z-10 w-full max-w-sm transition-all duration-700",
        entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      )}>
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <LogoMark size={64}/>
          <h1 className="text-2xl font-black mt-3 tracking-tight text-gradient-flow">StudoX Flow</h1>
          <p className="text-[10px] text-gray-600 mt-0.5 font-semibold tracking-widest uppercase">StudoX Flow</p>
        </div>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <StepDot active={step === "category"} done={step === "list"}/>
          <div className="w-8 h-px transition-all duration-300" style={{ background: step === "list" ? "#8b5cf6" : "rgba(255,255,255,0.1)" }}/>
          <StepDot active={step === "list"} done={false}/>
        </div>

        {/* ── STEP 1: Category selection ── */}
        {step === "category" && (
          <div className="animate-fade-in">
            <div className="text-center mb-6">
              <h2 className="text-lg font-black text-white">Einrichtungstyp wählen</h2>
              <p className="text-xs text-gray-500 mt-1">Über welche Art von Einrichtung möchtest du dich anmelden?</p>
            </div>

            <div className="space-y-2.5 mb-5">
              {CATEGORIES.map((cat, i) => (
                <CategoryCard
                  key={cat.id}
                  cat={cat}
                  delay={i * 40}
                  onClick={() => handleCategoryClick(cat)}
                />
              ))}
            </div>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-white/5"/>
              <span className="text-[10px] text-gray-700 font-semibold">oder</span>
              <div className="flex-1 h-px bg-white/5"/>
            </div>

            <button
              onClick={onPrivate}
              className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl border transition-all duration-300 group"
              style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.06)" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(6,182,212,0.4)"; e.currentTarget.style.background = "rgba(6,182,212,0.06)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#06b6d4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              <span className="text-sm font-bold text-gray-300 group-hover:text-white transition-colors">Privater Zugang</span>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>

            <p className="text-center text-[10px] text-gray-700 mt-5 leading-relaxed">
              Einrichtung noch nicht dabei?{" "}
              <button className="text-violet-500 hover:text-violet-400 transition-colors font-semibold" onClick={onPrivate}>
                Lehrer kontaktieren
              </button>
            </p>
          </div>
        )}

        {/* ── STEP 2: Specific institution list ── */}
        {step === "list" && cat && (
          <div className="animate-fade-in">
            {/* Back + header */}
            <div className="flex items-center gap-3 mb-5">
              <button
                onClick={() => { setStep("category"); setSelectedCat(null); }}
                className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200"
                style={{ background: "rgba(255,255,255,0.05)", color: "#9ca3af" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = cat.bg; e.currentTarget.style.color = cat.color; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "#9ca3af"; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
              <div className="flex-1">
                <h2 className="text-base font-black text-white">{cat.label} wählen</h2>
                <p className="text-[10px] text-gray-600">{cat.sub}</p>
              </div>
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: cat.bg, color: cat.color }}>
                <cat.Icon/>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Einrichtung suchen…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none transition-all duration-200"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                onFocus={(e) => { e.currentTarget.style.borderColor = cat.border; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; }}
              />
            </div>

            {/* List */}
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto scrollbar-hide pr-0.5">
              {loading ? (
                <div className="flex justify-center py-10">
                  <div className="w-6 h-6 rounded-full border-2 border-violet-500/30 border-t-violet-500 animate-spin"/>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(255,255,255,0.04)" }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#4b5563" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </div>
                  <p className="text-xs text-gray-600 font-medium">
                    {search ? `Keine Treffer für "${search}"` : "Noch keine Einrichtungen hinterlegt"}
                  </p>
                  <p className="text-[10px] text-gray-700 mt-1">Bitte wende dich an deinen Lehrer</p>
                </div>
              ) : (
                filtered.map((inst) => (
                  <InstitutionRow
                    key={inst.id}
                    inst={inst}
                    cat={cat}
                    onClick={() => onSelect({ ...inst, type: selectedCat.id, categoryLabel: cat.label })}
                  />
                ))
              )}
            </div>

            {/* Divider + private option */}
            {!loading && filtered.length > 0 && (
              <>
                <div className="flex items-center gap-3 mt-4 mb-3">
                  <div className="flex-1 h-px bg-white/5"/>
                  <span className="text-[10px] text-gray-700 font-semibold">nicht dabei?</span>
                  <div className="flex-1 h-px bg-white/5"/>
                </div>
                <button
                  onClick={onPrivate}
                  className="w-full py-2.5 rounded-xl text-xs font-bold text-gray-500 hover:text-gray-300 transition-colors"
                  style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}
                >
                  Privater Zugang nutzen
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Logo mark – Nexus Isometric Cube ──────────────────────────────────── */
export function LogoMark({ size = 56 }) {
  return (
    <img src="/studox-logo.png" alt="StudoX" height={size} width={Math.round(size * 0.67)}
      style={{ height: size, width: "auto", objectFit: "contain", filter: "drop-shadow(0 0 18px rgba(124,58,237,0.4))" }} />
  );
}
const _LogoMarkOld = ({ size = 56 }) => {
  return (
    <div style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="lm-bg" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#1a0533"/>
            <stop offset="100%" stopColor="#04091a"/>
          </linearGradient>
          {/* Top face: brightest */}
          <linearGradient id="lm-top" x1="40" y1="11" x2="40" y2="40" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#ede9fe"/>
            <stop offset="100%" stopColor="#a78bfa"/>
          </linearGradient>
          {/* Left face: purple */}
          <linearGradient id="lm-left" x1="16" y1="25" x2="40" y2="69" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#7c3aed"/>
            <stop offset="100%" stopColor="#2e0a6e"/>
          </linearGradient>
          {/* Right face: cyan */}
          <linearGradient id="lm-right" x1="64" y1="25" x2="40" y2="69" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#22d3ee"/>
            <stop offset="100%" stopColor="#075985"/>
          </linearGradient>
          {/* Rim */}
          <linearGradient id="lm-rim" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#a78bfa" stopOpacity="0.6"/>
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.25"/>
          </linearGradient>
          <filter id="lm-bg-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="5" result="b"/>
            <feFlood floodColor="#7c3aed" floodOpacity="0.55" result="c"/>
            <feComposite in="c" in2="b" operator="in" result="g"/>
            <feMerge><feMergeNode in="g"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="lm-dot" x="-150%" y="-150%" width="400%" height="400%">
            <feGaussianBlur stdDeviation="2.5" result="b"/>
            <feFlood floodColor="#22d3ee" floodOpacity="0.9" result="c"/>
            <feComposite in="c" in2="b" operator="in" result="g"/>
            <feMerge><feMergeNode in="g"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* Background pill with purple glow */}
        <rect x="2" y="2" width="76" height="76" rx="17" fill="url(#lm-bg)" filter="url(#lm-bg-glow)"/>
        <rect x="2" y="2" width="76" height="76" rx="17" fill="none" stroke="url(#lm-rim)" strokeWidth="1.5"/>

        {/* ── Isometric cube ── center (40,40), top at (40,11) */}
        {/* Vertices: top(40,11) topR(64,25) botR(64,55) bot(40,69) botL(16,55) topL(16,25) */}

        {/* Top face  – lightest */}
        <polygon points="40,11 64,25 40,40 16,25" fill="url(#lm-top)"/>
        {/* Left face – purple */}
        <polygon points="16,25 40,40 40,69 16,55" fill="url(#lm-left)"/>
        {/* Right face – cyan */}
        <polygon points="64,25 40,40 40,69 64,55" fill="url(#lm-right)"/>

        {/* Outer hex outline */}
        <polyline points="40,11 64,25 64,55 40,69 16,55 16,25 40,11"
          stroke="rgba(255,255,255,0.13)" strokeWidth="0.9" fill="none"/>
        {/* Inner edge lines from center */}
        <line x1="40" y1="11" x2="40" y2="40" stroke="rgba(255,255,255,0.22)" strokeWidth="0.9"/>
        <line x1="16" y1="25" x2="40" y2="40" stroke="rgba(255,255,255,0.12)" strokeWidth="0.9"/>
        <line x1="64" y1="25" x2="40" y2="40" stroke="rgba(255,255,255,0.12)" strokeWidth="0.9"/>
        <line x1="40" y1="40" x2="40" y2="69" stroke="rgba(255,255,255,0.10)" strokeWidth="0.9"/>
        <line x1="40" y1="40" x2="16" y2="55" stroke="rgba(255,255,255,0.08)" strokeWidth="0.9"/>
        <line x1="40" y1="40" x2="64" y2="55" stroke="rgba(255,255,255,0.08)" strokeWidth="0.9"/>

        {/* Specular highlight on top face */}
        <ellipse cx="35" cy="22" rx="9" ry="4.5" fill="white" opacity="0.22" transform="rotate(-28 35 22)"/>

        {/* Subtle top-face overlay for depth */}
        <polygon points="40,11 64,25 40,40 16,25" fill="white" opacity="0.04"/>

        {/* Cyan accent dot – top-right orbit */}
        <circle cx="62" cy="17" r="4.5" fill="#22d3ee" filter="url(#lm-dot)"/>
        <circle cx="62" cy="17" r="2"   fill="white" opacity="0.85"/>
      </svg>
    </div>
  );
}
